import { Header, Icon, Screen, Text } from '@components'
import { useLanguage } from '@context'
import { useTheme, useNavigation, useIsFocused } from '@react-navigation/native'
import { Theme } from '@theme'
import { showInfoToast } from '@utils'
import React, { useCallback, useEffect, useRef, useState } from 'react'
import { View, StyleSheet } from 'react-native'
import jsQR from 'jsqr'
import { eventCodeFromUrl } from '@utils'
import type { GlobalNavigationProp } from '@navigation'

const KEEPTOUCH_PROFILE_PATTERN = /\/profile\/([a-zA-Z0-9_-]+)/

// After a code that is not a KeepTouch profile, the scanner waits before it
// reads again, so its message is not repeated while the code stays in view.
const REARM_DELAY_MS = 3000

export function QRCodeScanner() {
  const navigation = useNavigation()
  const theme = useTheme()
  const { t } = useLanguage()
  const isFocused = useIsFocused()
  const styles = getScanStyles(theme)
  const videoRef = useRef<HTMLVideoElement | null>(null)
  const canvasRef = useRef<HTMLCanvasElement | null>(null)
  const streamRef = useRef<MediaStream | null>(null)
  const scanIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const [cameraActive, setCameraActive] = useState(false)
  const [permissionDenied, setPermissionDenied] = useState(false)
  const [isProcessing, setIsProcessing] = useState(false)
  // A detection already under way can answer after `isProcessing` has changed:
  // the ref is what stops a code from being handled twice.
  const processingRef = useRef(false)
  const rearmTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const handleScannedUrl = useCallback((url: string) => {
    if (processingRef.current) return
    processingRef.current = true
    setIsProcessing(true)

    const eventCode = eventCodeFromUrl(url)
    if (eventCode) {
      stopCamera()
      ;(navigation as GlobalNavigationProp).navigate('PublicEvent', { code: eventCode })
      return
    }

    const match = url.match(KEEPTOUCH_PROFILE_PATTERN)
    if (match) {
      const profileId = match[1]
      stopCamera()
      // Ouvre la fiche scannée (même schéma que le scanner natif). L'ancien
      // navigate('Profile', { profileId }) atterrissait sur SA propre fiche.
      ;(navigation as any).navigate('Profile', {
        screen: 'DetailsGhostProfile',
        params: { profileId },
      })
    } else {
      // Alert.alert shows nothing in a browser: say it in a toast and read again.
      showInfoToast(t('qrCode.info'), t('qrCode.notKeepTouchUrl'))
      rearmTimerRef.current = setTimeout(() => {
        processingRef.current = false
        setIsProcessing(false)
      }, REARM_DELAY_MS)
    }
  }, [navigation, t])

  const stopCamera = useCallback(() => {
    if (scanIntervalRef.current) {
      clearInterval(scanIntervalRef.current)
      scanIntervalRef.current = null
    }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop())
      streamRef.current = null
    }
    setCameraActive(false)
  }, [])

  const startCamera = useCallback(async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'environment' },
      })
      streamRef.current = stream
      if (videoRef.current) {
        videoRef.current.srcObject = stream
        videoRef.current.play()
        setCameraActive(true)
      }
    } catch (error) {
      setPermissionDenied(true)
    }
  }, [])

  const scanWithJsQR = useCallback((ctx: CanvasRenderingContext2D, width: number, height: number) => {
    const imageData = ctx.getImageData(0, 0, width, height)
    const result = jsQR(imageData.data, width, height)
    if (result && result.data) {
      handleScannedUrl(result.data)
    }
  }, [handleScannedUrl])

  const scanFrame = useCallback(() => {
    if (!videoRef.current || !canvasRef.current || isProcessing) return

    const video = videoRef.current
    const canvas = canvasRef.current
    const ctx = canvas.getContext('2d')
    if (!ctx || video.readyState !== video.HAVE_ENOUGH_DATA) return

    canvas.width = video.videoWidth
    canvas.height = video.videoHeight
    ctx.drawImage(video, 0, 0, canvas.width, canvas.height)

    // Use BarcodeDetector API if available, otherwise fall back to jsQR
    if ('BarcodeDetector' in window) {
      const detector = new (window as any).BarcodeDetector({ formats: ['qr_code'] })
      detector.detect(canvas).then((barcodes: any[]) => {
        if (barcodes.length > 0 && barcodes[0].rawValue) {
          handleScannedUrl(barcodes[0].rawValue)
        }
      }).catch(() => {
        // BarcodeDetector failed, try jsQR as fallback
        scanWithJsQR(ctx, canvas.width, canvas.height)
      })
    } else {
      scanWithJsQR(ctx, canvas.width, canvas.height)
    }
  }, [handleScannedUrl, isProcessing, scanWithJsQR])

  // The scanner stays mounted under the profile it opens: the camera is
  // released while it is covered, and it reads again as soon as it is back.
  useEffect(() => {
    if (!isFocused) return
    processingRef.current = false
    setIsProcessing(false)
    setPermissionDenied(false)
    startCamera()
    return () => {
      if (rearmTimerRef.current) {
        clearTimeout(rearmTimerRef.current)
        rearmTimerRef.current = null
      }
      stopCamera()
    }
  }, [isFocused])

  useEffect(() => {
    if (cameraActive && !isProcessing) {
      scanIntervalRef.current = setInterval(scanFrame, 500)
    }
    return () => {
      if (scanIntervalRef.current) {
        clearInterval(scanIntervalRef.current)
        scanIntervalRef.current = null
      }
    }
  }, [cameraActive, isProcessing, scanFrame])

  return (
    <Screen>
      <Header
        iconLeft="arrowBack"
        onPressLeft={() => {
          stopCamera()
          // Opened by its address (a reload): there is nothing to go back to.
          if (navigation.canGoBack()) navigation.goBack()
          else (navigation as any).navigate('QRCode')
        }}
        title={t('qrCode.scanTitle')}
      />

      <View style={styles.container}>
        {permissionDenied ? (
          <View style={styles.permissionContainer}>
            <Icon name="scan" size={100} color={theme.colors.greyBlue} />
            <Text type="bodyBold" color={theme.colors.primary} style={styles.permissionTitle}>
              {t('qrCode.cameraPermissionTitle')}
            </Text>
            <Text type="bodySmall" color={theme.colors.greyBlue} style={styles.permissionMessage}>
              {t('qrCode.cameraPermissionMessage')}
            </Text>
          </View>
        ) : (
          <View style={styles.cameraContainer}>
            {/* @ts-ignore - web-only elements */}
            <video
              ref={videoRef}
              style={{
                width: '100%',
                maxWidth: 500,
                borderRadius: 16,
                objectFit: 'cover',
              }}
              autoPlay
              playsInline
              muted
            />
            {/* Hidden canvas for frame processing */}
            {/* @ts-ignore - web-only elements */}
            <canvas ref={canvasRef} style={{ display: 'none' }} />
            <Text type="bodySmall" color={theme.colors.greyBlue} style={styles.instructionText}>
              {t('qrCode.scanInstruction')}
            </Text>
            {!('BarcodeDetector' in window) && (
              <Text type="bodySmall" color={theme.colors.greyBlue} style={styles.fallbackText}>
                {t('qrCode.scanWebFallbackActive')}
              </Text>
            )}
          </View>
        )}
      </View>
    </Screen>
  )
}

const getScanStyles = (theme: Theme) =>
  StyleSheet.create({
    container: {
      flex: 1,
      alignItems: 'center',
      justifyContent: 'center',
      paddingHorizontal: theme.spacing.lg,
    },
    permissionContainer: {
      alignItems: 'center',
      paddingHorizontal: theme.spacing.xl,
    },
    permissionTitle: {
      marginTop: theme.spacing.lg,
      textAlign: 'center',
    },
    permissionMessage: {
      marginTop: theme.spacing.sm,
      textAlign: 'center',
      lineHeight: 20,
    },
    cameraContainer: {
      alignItems: 'center',
      width: '100%',
    },
    instructionText: {
      marginTop: theme.spacing.lg,
      textAlign: 'center',
    },
    fallbackText: {
      marginTop: theme.spacing.sm,
      textAlign: 'center',
      fontStyle: 'italic',
    },
  })
